import React, { useImperativeHandle, forwardRef } from 'react';
import { useEmailValidation } from '@/hooks/validation';
import GoogleMaterialIcon from '@/components/ui/GoogleMaterialIcon';

/**
 * EmailValidationIndicator Component
 * 
 * Reusable component that combines email validation logic and visual feedback.
 * Shows real-time validation status (checking, available, errors) inline with form fields.
 * 
 * @example
 * ```tsx
 * <EmailValidationIndicator
 *   email={emailValue}
 *   enabled={true}
 *   currentEntityId={userId}
 *   entityType="user"
 *   onValidationChange={(isValid, message) => {
 *     setEmailValid(isValid);
 *   }}
 * />
 * ```
 */

interface EmailValidationIndicatorProps {
  /** Email value to validate */
  email: string;
  /** Whether validation is enabled */
  enabled?: boolean;
  /** Current entity ID (for checking duplicates excluding current record) */
  currentEntityId?: string;
  /** Entity type for duplicate checking */
  entityType?: 'athlete' | 'user' | 'staff' | 'parent' | 'athlete_invitation' | 'parent_invitation' | 'organization' | 'gym_location' | 'venue';
  /** Callback when validation result changes */
  onValidationChange?: (isValid: boolean, message: string) => void;
  /** Additional checking state (for legacy duplicate checking) */
  isCheckingDuplicate?: boolean;
  /** Whether there's a display error (hides "Available" indicator) */
  hasError?: boolean;
  /** Custom className for positioning */
  className?: string;
  /** Debounce delay in milliseconds */
  debounceMs?: number;
  /** When to trigger validation */
  validationTrigger?: 'onChange' | 'onBlur';
  /**
   * #2300: conflict reason codes (EMAIL_UNAVAILABLE_REASONS) this form treats
   * as ADVISORY rather than blocking. A match reports `isValid: true` through
   * onValidationChange, so the field shows no error and the form is not gated.
   *
   * Opt-in by design: omitting it (every existing caller) keeps the historical
   * "any conflict blocks" behavior exactly as-is. It controls GATING only —
   * `onNotice` reports conflicts either way.
   */
  nonBlockingReasons?: readonly string[];
  /**
   * Reports the server's conflict verdict with its machine-readable reason —
   * for blocking and advisory conflicts alike — and null once the address is
   * clean or validation is cleared. Lets a form tailor its recovery UI to the
   * specific conflict instead of guessing from the copy.
   */
  onNotice?: (notice: { reason: string; message: string; blocking: boolean } | null) => void;
}

export interface EmailValidationIndicatorRef {
  triggerValidation: () => void;
  clearValidation: () => void;
}

const EmailValidationIndicator = forwardRef<EmailValidationIndicatorRef, EmailValidationIndicatorProps>(({
  email,
  enabled = true,
  currentEntityId,
  entityType = 'athlete',
  onValidationChange,
  isCheckingDuplicate = false,
  hasError = false,
  className = 'mt-1.5 flex items-center',
  debounceMs = 800,
  validationTrigger = 'onBlur',
  nonBlockingReasons,
  onNotice,
}, ref) => {
  // Track validation result locally
  const [apiValidationResult, setApiValidationResult] = React.useState<{
    available: boolean;
    error?: string;
    reason?: string;
  } | null>(null);

  // Keep the latest callbacks in a ref so the hook's onValidationResult closure
  // never goes stale and changing an inline arrow prop can't re-run validation.
  const onValidationChangeRef = React.useRef(onValidationChange);
  const onNoticeRef = React.useRef(onNotice);
  const nonBlockingReasonsRef = React.useRef(nonBlockingReasons);
  React.useEffect(() => {
    onValidationChangeRef.current = onValidationChange;
    onNoticeRef.current = onNotice;
    nonBlockingReasonsRef.current = nonBlockingReasons;
  });

  // Real-time email validation hook
  const { validateEmail: validateEmailRealTime, clearValidation, isChecking, lastResult } = useEmailValidation({
    debounceMs,
    onValidationResult: (result) => {
      setApiValidationResult(result);
      if (result) {
        if (!result.available) {
          // #2300: an advisory conflict (e.g. the parent's OWN pending invite)
          // must inform without gating. Anything else keeps blocking as before.
          const advisory =
            !!result.reason && !!nonBlockingReasonsRef.current?.includes(result.reason);

          onNoticeRef.current?.({
            reason: result.reason || '',
            message: result.error || 'Email is already in use',
            blocking: !advisory,
          });
          onValidationChangeRef.current?.(
            advisory,
            advisory ? '' : result.error || 'Email is already in use'
          );
        } else {
          // Email is available
          onNoticeRef.current?.(null);
          onValidationChangeRef.current?.(true, '');
        }
      }
    }
  });

  // Helper function to check if email looks complete enough for real-time validation
  const isEmailCompleteEnough = (emailValue: string): boolean => {
    const parts = emailValue.split('@');
    if (parts.length !== 2) return false;
    
    const [localPart, domainPart] = parts;
    if (!localPart || !domainPart) return false;
    
    const domainParts = domainPart.split('.');
    if (domainParts.length < 2) return false;
    
    const tld = domainParts[domainParts.length - 1];
    if (tld.length < 2) return false;
    
    return domainParts.every(part => part.length > 0);
  };

  // Trigger validation when email changes (for onChange trigger)
  React.useEffect(() => {
    // A DISABLED validator reports nothing at all. It previously emitted
    // onNotice(null) here, and this effect re-runs on every render (its
    // `validateEmailRealTime` dep is rebuilt each time), so a form with
    // enabled={false} — the ?invitationEmail= family-guardian signup, whose
    // address is locked — got a continuous stream of "no conflict" signals that
    // wiped its own submit-time duplicate-email banner before the parent could
    // read it. Silence is the correct output for a validator that isn't running.
    if (!enabled) return;

    if (!email) {
      clearValidation();
      setApiValidationResult(null);
      onNoticeRef.current?.(null);
      return;
    }

    if (validationTrigger === 'onChange' && isEmailCompleteEnough(email)) {
      validateEmailRealTime(email, currentEntityId, entityType);
    } else if (validationTrigger === 'onChange') {
      clearValidation();
      setApiValidationResult(null);
      onNoticeRef.current?.(null);
    }
  }, [email, enabled, validationTrigger, currentEntityId, entityType, validateEmailRealTime, clearValidation]);

  // Expose manual validation trigger for onBlur scenarios
  const triggerValidation = React.useCallback(() => {
    if (enabled && email && isEmailCompleteEnough(email)) {
      validateEmailRealTime(email, currentEntityId, entityType);
    }
  }, [enabled, email, currentEntityId, entityType, validateEmailRealTime, isEmailCompleteEnough]);

  // Expose clear function
  const clear = React.useCallback(() => {
    clearValidation();
    setApiValidationResult(null);
    onNoticeRef.current?.(null);
  }, [clearValidation]);

  // Expose methods via ref for parent components
  useImperativeHandle(ref, () => ({
    triggerValidation,
    clearValidation: clear,
  }), [triggerValidation, clear]);

  // Don't render if disabled or no email value
  if (!enabled || !email) {
    return null;
  }

  // Show checking state
  if (isChecking || isCheckingDuplicate) {
    return (
      <div className={className}>
        <div className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400">
          <GoogleMaterialIcon name="progress_activity" size="xs" className="animate-spin" />
          <span>Checking...</span>
        </div>
      </div>
    );
  }

  // Show available state (only if no errors)
  if (!hasError && apiValidationResult && apiValidationResult.available) {
    return (
      <div className={className}>
        <div className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
          <GoogleMaterialIcon name="check_circle" size="xs" />
          <span>Available</span>
        </div>
      </div>
    );
  }

  // No indicator when validation hasn't run or email is invalid
  return null;
});

// Export hook functions for manual control
EmailValidationIndicator.displayName = 'EmailValidationIndicator';

export default EmailValidationIndicator;

// Export types for use in other components
export type { EmailValidationIndicatorProps };

